import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import fastIsEqual from 'react-fast-compare';
import styled from 'styled-components';
import { ObjectManager } from '../../../concepts/objects';
import { Rect } from '../../../platform/geom/Rect';
import { useResolve } from '../../Container';
import { PropertyFacade } from '../../canvas/properties/PropertyFacade';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Colors } from './../../../ui';
import { Shape } from './Shape';
import { ShapeAlgorithm, ShapeSettings } from './ShapeAlgorithm';
import { ShapeKind, ShapeStyle } from './ShapeEnums';

const XScrollbars = Scrollbars as any;

type ShapeItem = ShapeSettings & {
    kind: ShapeKind,
    paths: any[];
    padding: number;
};

const OptionFrame = styled.div<{ width: number, height: number }>`
    position: relative;
    width: ${p => p.width}px;
    height: ${p => p.height}px;
    border-radius: .125rem;
    cursor: pointer;

    &:hover {
        background: ${Colors.hover};
    }
    &:active {
        background: ${Colors.active};
    }
`;

type ShapeOptionSelectCallback = (item: ShapeItem, final?: boolean) => void;
const ShapeOption = ({ item, onSelect }: { item: ShapeItem, onSelect: ShapeOptionSelectCallback }) => {
    const offset = 10;
    const frame = new Rect(0, 0, item.width, item.height).inflate(item.padding);
    const svg = frame.inflate(offset);

    const svgStyle = {
        position: 'absolute' as const,
        left: -offset,
        top: -offset,
        PointerEvents: 'none' as const,
    };

    const applyShadow = Colors.isLight(item.color, 4);

    const onMouseEnter = () => onSelect(item, false);
    const onClick = () => onSelect(item, true);

    return <OptionFrame width={frame.width} height={frame.height} {...{ onMouseEnter, onClick }}>
        <svg style={svgStyle} width={svg.width} height={svg.height} viewBox={`0 0 ${svg.width} ${svg.height}`}>
            {applyShadow && <defs>
                <filter id="shadow" colorInterpolationFilters="sRGB" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="0" stdDeviation="2" floodOpacity="0.2" />
                </filter>
            </defs>}
            <g transform={`translate(${offset + item.padding}, ${offset + item.padding})`}>
                {item.paths.map(({ d, ...props }: any, i) => <path filter="url(#shadow)" key={i} d={d} {...props} strokeWidth={2} />)}
            </g>
        </svg>
    </OptionFrame>
};

const useShapeOptions = (background: string, style: any) => useMemo(() => {

    const base = 32;
    const padding = 5;

    const opt1x1 = {
        padding,
        width: base,
        height: base,
        cornerRadius: 0,
        sides: 3,
        stroke: 1,
        color: background ?? Colors.primaryLight,
        style: style ?? ShapeStyle.fill,
    };

    const opt2x1 = {
        ...opt1x1,
        width: (base * 2) + (padding * 2),
    };

    const opt3x1 = {
        ...opt1x1,
        width: (base * 3) + (padding * 4),
    };

    const shapeOpts = [
        ShapeKind.rectangle,
        ShapeKind.oval,
        ShapeKind.triangle,
        ShapeKind.rightangle,
        ShapeKind.rhombus,
        ShapeKind.pentagon,
        ShapeKind.hexagon,
        ShapeKind.octagon,
        ShapeKind.paragram,
        ShapeKind.heart,
        ShapeKind.quote,
        ShapeKind.trapezoid,
    ];

    const bannerOpts = [
        ShapeKind.banner1,
        ShapeKind.banner2,
        ShapeKind.banner3,
        ShapeKind.banner4,
        ShapeKind.banner5,
        ShapeKind.banner6,
    ];

    const starOpts = [
        3, 4, 5, 6, 8, 12
    ];
    const badgeOpts = [
        3, 4, 5, 6, 8, 12
    ];

    const arrowOpts = [
        ShapeKind.arrow1,
        ShapeKind.arrow2,
        ShapeKind.arrow3,
        ShapeKind.arrow4,
    ];

    const shapes = shapeOpts.map(x => ({
        ...opt1x1,
        kind: x,
        ...ShapeAlgorithm.for(x)(opt1x1),
    }));

    const banners = bannerOpts.map(x => ({
        ...opt2x1,
        kind: x,
        ...ShapeAlgorithm.for(x)(opt2x1),
    }));

    const stars = starOpts.map(x => ({
        ...opt1x1,
        sides: x,
        kind: ShapeKind.star,
        ...ShapeAlgorithm.for(ShapeKind.star)({ ...opt1x1, sides: x }),
    }));

    const badges = badgeOpts.map(x => ({
        ...opt1x1,
        sides: x,
        kind: ShapeKind.badge,
        ...ShapeAlgorithm.for(ShapeKind.badge)({ ...opt1x1, sides: x }),
    }));

    const arrows = arrowOpts.map(x => ({
        ...opt3x1,
        kind: x,
        ...ShapeAlgorithm.for(x)(opt3x1),
    }));

    return [
        shapes,
        banners,
        stars,
        badges,
        arrows
    ];

}, [background, style]);

export const ShapeKindWidget = ({ widget }: { widget: PopoutWidget }) => {

    const objects = useResolve(ObjectManager);
    const facade = widget.facade as PropertyFacade<Shape>;

    const resetOnUnmout = useRef(true);
    const originalValue = useMemo(() => ({
        kind: facade.get('kind'),
        sides: facade.get('sides'),
    }), []);

    const [value, setValue] = useState(originalValue);

    const rows = useShapeOptions(
        facade.get('background'),
        facade.get('style'),
    );

    const applyValue = (value: any) => {
        setValue(value);
        objects.update(u => {
            u.rolling(facade.keyFor('kind', 'sides'));
            facade.set('kind', value.kind);
            facade.set('sides', value.sides);
        });
    }

    const onSelect = useCallback((item: ShapeItem, final?: boolean) => {
        const newValue = {
            ...originalValue,
            kind: item.kind,
            sides: item.sides,
        };

        applyValue(newValue);

        if (final) {
            resetOnUnmout.current = false;
        };

        widget.update({ value: newValue, open: !final });

    }, [widget]);

    const onReset = () => {
        if (!fastIsEqual(value, originalValue)) {
            applyValue(originalValue);
        }
    };

    useEffect(() =>
        () => {
            if (resetOnUnmout.current) {
                applyValue(originalValue);
            }
        }, []);

    return <Flexer padding=".5" width="17.5" height="15" onMouseLeave={onReset}>
        <XScrollbars className="scrollbars">
            {rows.map((r, i) =>
                <Flexer key={i} flex="row" wrap="wrap" gap="0" color={Colors.text}>
                    {r.map((o, i) => <ShapeOption key={i} item={o} onSelect={onSelect} />)}
                </Flexer>
            )}
        </XScrollbars>
    </Flexer>

    // private onSelect = (kind: ShapeKind) => () => {
    //     // This is hacky AF, I'm sorry:
    //     this.props.widget.update({
    //         open: false,
    //         value: kind,
    //     });
    // };
}
